import {
  SourceOfWealthType,
  SourceOfWealthFrequency,
  sourceOfWealthFrequencies,
  sourceOfWealthTypes,
} from "@goodfill/domain-customer-application-types";
import { capitalizeEveryFirstWordLetter } from "@goodfill/string";
import { Grid, InputSelect, Textarea, InputSelectOption } from "@goodfill/ui-components";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { FieldGroup } from "../../../../../../form/FieldGroup";
import { Link } from "../../../../../../ui-components/Link/Link";
import { defaultSourceOfWealth, FormSourceOfWealth } from "../../../formState/formSourceOfWealth";
import { CustomerApplicationFormState } from "../../../formState/formState";
import { FormYesOrNoQuestion } from "../../FormYesOrNoQuestion";
import { TrashCanIcon } from "./assets/TrashCanIcon";

const sourceOfWealthOptions: InputSelectOption[] = sourceOfWealthTypes.map((value) => ({
  value,
  label: capitalizeEveryFirstWordLetter(value.replace("-", " ")),
}));

const wealthFrequencyOptions: InputSelectOption[] = sourceOfWealthFrequencies.map((value) => ({
  value,
  label: capitalizeEveryFirstWordLetter(value.replace("-", " ")),
}));

export const SourcesOfWealthRows = ({ areAllDisabled }: { areAllDisabled: boolean }) => {
  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext<CustomerApplicationFormState>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "step5.sourcesOfWealth",
  });

  return (
    <>
      <div className="flex flex-col gap-6 md:gap-12">
        {fields.map((item, i) => (
          <Grid cols={2} gap={0.75} key={item.id}>
            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label={<>Source of Wealth {i + 1}</>}
                  validation={error?.message ? [error.message] : []}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <InputSelect
                    placeholder="Enter Source"
                    options={sourceOfWealthOptions}
                    value={field.value ?? ""}
                    onChange={(value) => {
                      field.onChange(value as SourceOfWealthType);
                      // as cast hack needed, see: https://react-hook-form.com/api/usefieldarray
                      trigger(`step5.sourcesOfWealth.${i}.description` as `step5.sourcesOfWealth.0.description`);
                    }}
                    border={error === undefined ? "neutral" : "red"}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    id={`sourcesOfWealth${i}WealthType`}
                    disabled={areAllDisabled}
                  />
                </FieldGroup>
              )}
              // as cast hack needed, see: https://react-hook-form.com/api/usefieldarray
              name={`step5.sourcesOfWealth.${i}.wealthType` as `step5.sourcesOfWealth.0.wealthType`}
              control={control}
            />

            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label="Description"
                  validation={error?.message ? [error.message] : []}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <Textarea
                    height={24}
                    value={field.value}
                    setValue={field.onChange}
                    border={error === undefined ? "neutral" : "red"}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    id={`sourcesOfWealth${i}Description`}
                    disabled={areAllDisabled}
                  />
                </FieldGroup>
              )}
              // as cast hack needed, see: https://react-hook-form.com/api/usefieldarray
              name={`step5.sourcesOfWealth.${i}.description` as `step5.sourcesOfWealth.0.description`}
              control={control}
            />

            <Controller
              render={({ field, fieldState: { error } }) => (
                <FormYesOrNoQuestion
                  id={`sourcesOfWealth${i}IsUsedForFunds`}
                  label="Is wealth used for funds?"
                  onChange={field.onChange}
                  value={field.value}
                  validation={error?.message ? [error.message] : []}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  disabled={areAllDisabled}
                />
              )}
              // as cast hack needed, see: https://react-hook-form.com/api/usefieldarray
              name={`step5.sourcesOfWealth.${i}.isUsedForFunds` as `step5.sourcesOfWealth.0.isUsedForFunds`}
              control={control}
            />

            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label="Frequency"
                  validation={error?.message ? [error.message] : []}
                  fill
                  className="w-80-sm w-full-after-md"
                >
                  <InputSelect
                    placeholder="Select frequency"
                    options={wealthFrequencyOptions}
                    selected={field.value}
                    onChange={(value) => field.onChange(value as SourceOfWealthFrequency)}
                    border={error === undefined ? "neutral" : "red"}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    id={`sourcesOfWealth${i}Frequency`}
                    disabled={areAllDisabled}
                  />
                </FieldGroup>
              )}
              // as cast hack needed, see: https://react-hook-form.com/api/usefieldarray
              name={`step5.sourcesOfWealth.${i}.frequency` as `step5.sourcesOfWealth.0.frequency`}
              control={control}
            />
          </Grid>
        ))}
      </div>
      <FieldGroup
        label={""}
        validation={errors?.step5?.sourcesOfWealth?.message ? [errors?.step5?.sourcesOfWealth?.message] : []}
      >
        &nbsp;
      </FieldGroup>
      <div className="flex w-full justify-between items-stretch">
        {/* This `as FormSourceOfWealth` cast is ugly hack, it may break something */}
        {fields.length < 9 ? (
          <Link
            onClick={() => !areAllDisabled && append(defaultSourceOfWealth as FormSourceOfWealth)}
            id="addMoreSourceOfWealth"
          >
            +&nbsp;Add&nbsp;more
          </Link>
        ) : (
          <div />
        )}
        {fields.length > 1 ? (
          <Link red onClick={() => !areAllDisabled && remove(fields.length - 1)} id="removeSourceOfWealth">
            <div className="flex gap-1 md:mr-16">
              <TrashCanIcon />
              <p style={{ margin: "1px" }}>&nbsp;Remove&nbsp;Source of Wealth</p>
            </div>
          </Link>
        ) : null}
      </div>
    </>
  );
};
